/* eslint-disable @typescript-eslint/naming-convention */
import { RliPreHook } from '@rocketlane/rli-internal-sdk';
import { AxiosRequestConfig } from 'axios';
import { Moment } from 'moment';
import { stringify } from 'query-string';
import { AiAgentRegistry } from 'api/agents/types';
import type { Approval } from 'api/approval';
import {
  ExportPayload,
  ExportRequest,
  ExportResponse,
  FilterBy,
  ExportStatusType,
} from 'api/async-export';
import { CustomerCompany } from 'api/customer-company/types';
import type { SimpleSearchFilter } from 'api/dashboard/widget';
import {
  FieldTypeDisplay,
  FieldSectionSubType,
  FieldSectionType,
} from 'api/field-sections/types';
import type { AccountFields, RatingFieldScale } from 'api/fields/fields';
import {
  UserStatus,
  Company,
  DateType,
  ProjectId,
  Team,
  User,
} from 'api/models';
import { Sprint, SprintPoints } from 'api/sprint-planning/types';
import type { FieldFilter, GroupCriteria } from 'api/tasks/filter-types';
import { QueryCombinerType } from 'api/tasks/filter-types';
import type {
  PaginatedRequestParams,
  PaginatedResponse,
  PaginatedTaskCountResponse,
} from 'api/types';
import type { Color } from 'components/colors';
import type { Filter } from 'components/molecules/filters/types';
import type {
  SheetData,
  SheetViewType,
} from 'components/organisms/sheets/sheet-types';
import type { Actor } from 'data/core';
import { TaskEvents } from 'events/tasks';
import { InitWorkerRequestType, InitWorkerApi } from 'initWorker';
import { fieldAccessMetaToBase3 } from 'utils/field-access';
import { constructUrl } from 'utils/http-utilts';
import { getSimpleSearchFilterModule } from 'utils/simple-search-filter/module';
import type { FileAttachment } from '../attachments';
import type {
  Project,
  ProjectBudgetItem,
  ProjectBudgetItemLite,
  ProjectPhase,
} from '../project';
import { WithNotStrategicTransformer } from './with-non-strategic-partner';

export { TaskApprovalAPI } from './approval';

// TODO: replace with generic status? ProjectPhaseStatus looks the same
// @abhilashlr @Shajansheriff
export enum TaskStatus {
  ToDo = 'Todo',
  InProgress = 'In progress',
  Completed = 'Completed',
  Blocked = 'Blocked',
  Proposed = 'Proposed',
  InPlanning = 'In Planning',
  ToBeStaffed = 'To be Staffed',
  Cancelled = 'Cancelled',
}

export enum TaskCardPosition {
  Top = 'top',
  Bottom = 'bottom',
}

export enum TaskType {
  Task = 'TASK',
  Milestone = 'MILESTONE',
} // TODO:: This is just sample implementation, we can configure once we have use case.
export type FieldValueType = string | number | boolean | number[] | null;

interface AssigneeAudit {
  assignedAt: number;
  assignedBy: User;
  assignee: User | Team;
  assigneeType: 'USER' | 'TEAM';
}

export enum TaskEffortSetting {
  Equal = 'EQUAL_SPLIT',
  Custom = 'UNEQUAL_SPLIT',
}

export interface TaskEffortMeta {
  teamId?: number;
  userId?: number;
  effort: number;
  projectFinancialsBudgetId?: number;
  includeSaturday?: boolean;
  includeSunday?: boolean;
}

export interface EnrichedTaskEffortMeta extends TaskEffortMeta {
  team?: Team;
  user?: User;
  responsibleCompany?: Company;
}

export enum TaskSourceType {
  Document = 'DOCUMENT',
  DocumentSection = 'DOCUMENT_SECTION',
  SpaceTab = 'SPACE_TAB',
  Default = 'DEFAULT',
  Template = 'TEMPLATE',
}

export type ExternalResourceInputs = Record<string, string | string[]> & {
  issuetype: string;
  projectId: string;
  epicId: string;
};

export type LinkedResource = {
  resourceMappingId: number;
  integrationId: number;
  nativeResourceType: string;
  nativeResourceId: number;
  externalResourceType: string;
  externalResourceId: string;
  externalIntegrationType: string;
  externalResourceInputs: ExternalResourceInputs;
  enabled: boolean;
  deleted: boolean;
  value?: string;
  context?: {
    jiraProjectId?: string;
    jiraIssueUrl?: string;
  };
};

interface Source {
  sourceType: TaskSourceType;
  sourceId: number;
  metaData?: {
    [key: string]: string | number;
  };
}

/** Budget status on rev-rec plan lines: field row; selected option is under `metaFieldValue` (e.g. color, label). */
export type RevenueRecognitionPlanBudgetStatus = Pick<
  Field,
  'fieldName' | 'metaFieldValue'
>;

export interface RevenueRecognitionPlanItem {
  budgetId: number;
  budgetName: string;
  budgetStatus?: RevenueRecognitionPlanBudgetStatus | null;
  recognisedPercentage: number;
  recognisedAmount: number;
}
export type AgentRunStepStatus =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'failed';

export interface AgentRunStep {
  title: string;
  status: AgentRunStepStatus;
  description: string;
}

export interface AgentRunSummary {
  steps: AgentRunStep[];
}

export interface TaskEntityMeta {
  id?: number;
  name?: string;
  entityType?: 'migration_agent' | 'workforce_agent';
  latestRunSummary?: AgentRunSummary;
  latestRunReferenceId?: string;
}

export interface Task {
  taskId: number;
  taskName: string;
  taskDescription?: string;
  taskCardPosition?: TaskCardPosition;
  privateTaskDescription?: string;
  status: TaskStatus;
  assignee?: AssigneeValue;
  agents?: AiAgentRegistry[];
  followers?: AssigneeValue; // followers and assignee are similar datatype
  dueDate?: DateType | null;
  startDate?: DateType | null;
  startDateActual?: DateType;
  dependentTasks: Array<Task>;
  subTaskOrder?: number[];
  subTasks?: Array<Task>;
  project: Project;
  fields: Array<FieldValue<FieldValueType>>;
  attachments?: Array<FileAttachment>;
  effort?: number;
  taskEffortSetting?: TaskEffortSetting;
  taskEffortMeta?: TaskEffortMeta[];
  atRisk?: boolean;
  // TODO: This should be projectTask?: number;
  parentTaskId?: number;
  parentTask?: number;
  parentTaskObject?: Task;
  private: boolean;
  approvals?: Array<Approval>;
  projectPhase?: ProjectPhase;
  completedAt?: number | null;
  assigneeAuditMeta: AssigneeAudit[];
  source?: Source;
  sources?: Source[];
  sourceId?: number;
  type: TaskType;
  progress: number;
  isCsatEnabled: boolean;
  responsible: Company[];
  csatRatings?: CsatRating[];
  createdBy: number;
  createdAt: number;
  linkedResources?: LinkedResource[];
  formInstanceId?: number;
  formDetails?: {
    headerImage?: string;
    responseCount?: number;
    formAnswerId?: number;
  };
  /** Timesheet fields */
  defaultTimeEntryBillable?: boolean;
  defaultTimeEntryCategory?: number;
  externalRefKey?: string;
  fieldsAccessMeta?: FieldAccessMeta;
  fieldsAccessMetaInBase3?: FieldAccessMetaInBase3;
  currentSprint?: { sprintId: number; sprintName: string };
  associatedSprints?: Pick<Sprint, 'sprintId' | 'sprintName'>[];
  isMilestoneRevenueRecognitionTask?: boolean;
  milestoneTaskRevenueRecognitionAmount?: number;
  revenueRecognitionPlans?: RevenueRecognitionPlanItem[];
  overrideTaskBillable?: boolean;
  overrideTimeEntryBillable?: boolean;
  overrideTaskCategory?: boolean;
  overrideTimeEntryCategory?: boolean;
  dependencyType?: string;
  lag?: number | null;
  hasApprovals?: boolean;
  epic?: {
    epicId: number;
    epicName: string;
  };
  deleted?: boolean;
  timeEntryMinutes?: number;
  totalAllocatedMinutes?: number | null;
  projectFinancialsBudgetId?: number;
  projectFinancialsBudget?: ProjectBudgetItem;
  projectFinancialsBudgets?: ProjectBudgetItemLite[];
  sheetDetails?: SheetData;
  sheetId?: number;
  remainingEffortInMinutes?: number;
  entityId?: number;
  entityType?: 'migration_agent' | 'workforce_agent';
  entityMeta?: TaskEntityMeta;
}

export interface DuplicateTaskProps {
  duplicateCount: number;
  baseTaskId: number;
  itemsToDuplicate: string[];
  taskNames: string[];
  projectPhaseId: number;
}

export interface CsatRating {
  rating: number;
  ratedAt: number;
  ratedBy: User;
  comment?: string;
}

export type DependentTask = AtLeast<
  Task,
  'taskId' | 'taskName' | 'project' | 'dependencyType' | 'lag'
>;

/* NOTE:: We are ITaskWithUid because we need a identifier to identify sub task.
when user create a new sub task, even before the create Promise has been resolved
we will push the record to array (state) and once the promise is resolved,
we will the query the temp sub task and push it with actual data.
TODO:: Find a better a way (equivalent to ember's store.createRecord()) to handle this
 */
export interface TaskWithUid extends Task {
  uid?: string;
}

export interface FieldValue<T> {
  fieldId: number;
  fieldName?: string;
  fieldValue: T;
  formattedFieldValue?: T;
  metaFieldValue?: any;
  fieldValueUser?: User;
  customField?: boolean;
  userCreated?: boolean;
  isEditable?: boolean;
}

export interface AssigneeValue {
  users: Array<User>;
  teams: Array<Team>;
}

export interface FieldOptionBase<T = number> {
  color?: Color;
  value: T;
  label: string;
  position?: number;
  active?: boolean;
  userStatus?: UserStatus;
  valueStr?: string;
}

export interface FieldOption<T> extends FieldOptionBase {
  color: Color;
  group: T;
}

export enum TaskStatusGroup {
  Todo = 1,
  InProgress,
  Completed,
  Blocked,
}

export enum StatusGroup {
  Todo = 1,
  InProgress,
  Completed,
  Blocked,
  Proposed = 5,
  InPlanning,
  ToBeStaffed,
  Cancelled,
}

export enum ProjectStatusGroup {
  Todo = 5,
  InProgress = 2,
  Completed = 3,
  'On hold' = 4,
  Cancelled = 8,
}

export enum BudgetStatusGroup {
  Proposed = 1,
  Active = 2,
  Completed = 3,
}

export enum FieldClass {
  Native = 'NATIVE',
  Custom = 'CUSTOM',
}

export enum CustomFieldValueSourceType {
  Manual = 'MANUAL',
  Inherited = 'INHERITED',
}

export type StatusFieldOption = Required<FieldOption<StatusGroup>>;
export type Decimals = number[];
export type FieldAccessMeta = Record<FieldSectionType, Decimals>;
export type Base3Number = number;
export type FieldAccessMetaInBase3 = Record<
  FieldSectionType | FieldSectionSubType,
  Base3Number[]
>;
export interface InheritableFieldsInfo {
  userFieldsInfo: Record<number, Pick<User, 'userId' | 'fields'>>;
  projectFieldsInfo: Record<number, Pick<Project, 'projectId' | 'fields'>>;
  taskFieldsInfo: Record<number, Pick<Task, 'taskId' | 'fields'>>;
  companyProjectIdsInfo: Record<number, number>;
  accountFieldsInfo: Record<
    number,
    Pick<CustomerCompany, 'companyId' | 'fields'>
  >;
}
export interface Field {
  isEnabled: boolean;
  customField: boolean;
  fieldId: number;
  fieldName: string;
  fieldLabel?: string;
  fieldIndex?: number;
  description?: string;
  fieldType: string;
  fieldDataType: string;
  /** @deprecated */
  /* TO CHECK: need this to check default field or user-generated */
  fieldGroup?: string;
  private: boolean;
  optionsAvailable: boolean;
  fieldSectionId?: number;
  updatedBy?: number;
  updatedAt?: number;
  createdBy?: number;
  createdAt?: number;
  metaData?: {
    default?: FieldValueType;
    options?: Array<FieldOption<number | StatusGroup>>;
    ratingScale?: RatingFieldScale;
    relatedFieldId?: number;
    startKeyEventId?: number;
    endKeyEventId?: number;
    intervalBenchmark?: number;
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    pointType?: SprintPoints;
    tags?: Array<FieldOption<number>>;
    roleIds?: number[];
    visibleOnInvoice?: boolean;
    fieldValueSourceFieldInfo?: Field;
    isMandatory?: boolean;
    fieldValueSourceType?: CustomFieldValueSourceType;
    fallbackFieldValue?: any;
  };
  objectType:
    | FieldSectionType.Project
    | FieldSectionType.Task
    | FieldSectionType.Users
    | FieldSectionType.ProjectPhase
    | FieldSectionType.Timeoff
    | FieldSectionType.Sprint
    | FieldSectionType.Financial
    | FieldSectionType.Company
    | FieldSectionType.Invoice
    | FieldSectionType.CreditNote
    | FieldSectionType.Budget
    | FieldSectionType.Epic
    | FieldSectionType.Backlog
    | FieldSectionType.TimeEntry
    | FieldSectionType.Expense
    | FieldSectionType.ResourceAllocation
    | FieldSectionType.Reports;
  objectSubType?: FieldSectionSubType;
  fieldTypeDisplay?: FieldTypeDisplay;
  userGroupIds: number[];
  isEditable?: boolean;
  formulaFieldMeta?: SimpleFormulaFieldMeta | ConditionalFormulaFieldMeta;
  userCreated?: boolean;
  sheetType?: SheetViewType;
  showTimePeriodFilterInUI?: boolean;
  systemField?: boolean;
  metaFieldValue?: any;
}

enum FormulaType {
  Simple = 'SIMPLE',
  Conditional = 'CONDITIONAL',
}

export interface FormulaPlaceHolder {
  fieldId?: number;
  fieldName?: string;
  placeholderName: string;
  source: string;
  aggType?: string;
  fieldType: string;
  fieldDataType?: string;
  aggregateCondition?: SimpleSearchFilter;
}

export interface FormulaExpression {
  expression: string;
  renderableExpression: string;
}

interface SimpleFormula extends FormulaExpression {
  placeholderMap: Record<string, FormulaPlaceHolder>;
  formulaConfig?: {
    excludeWeekends: boolean;
  };
}

export interface FormulaConditionalBlock {
  condition: SimpleSearchFilter;
  simpleFormula: FormulaExpression;
}

export interface ConditionalFormula {
  ifBlock: FormulaConditionalBlock;
  elseIfBlocks?: (FormulaConditionalBlock & { blockId: number })[];
  elseBlock: FormulaExpression;
  placeholderMap: Record<string, FormulaPlaceHolder>;
}

interface FormulaFieldMetaBase {
  fieldId?: number;
  triggerable?: boolean;
}

export interface SimpleFormulaFieldMeta extends FormulaFieldMetaBase {
  simpleFormulaConfig: SimpleFormula;
  formulaType: FormulaType.Simple;
}

export interface ConditionalFormulaFieldMeta extends FormulaFieldMetaBase {
  conditionalFormulaConfig: ConditionalFormula;
  formulaType: FormulaType.Conditional;
}

export interface StatusField extends Field {
  fieldValue: number;
  metaData: {
    default: StatusGroup;
    options: StatusFieldOption[];
  };
}

export interface FieldWithValue extends Field {
  value?: FieldValueType;
}

export interface FieldsWithUniqueValuesForBulkEditor {
  fieldId: number;
  value: FieldValueType;
}

// :TODO define type for all possible conditions
export interface TaskFilterCondition {
  fieldName: string;
  operator: string;
  fieldValue: any;
}

export interface UpdateSubTaskOrderPayload {
  subTaskOrder: number[];
}

export interface GroupExportProps {
  filters: GroupCriteria[];
  match: QueryCombinerType;
  columnOrder: string[];
  projectId?: number;
}

export interface GroupExportPropsV2 {
  filters: SimpleSearchFilter;
  match: QueryCombinerType;
  columnOrder: string[];
  projectId?: number;
}
export interface ExportProps {
  filters: FieldFilter[];
  match: QueryCombinerType;
  columnOrder: string[];
  projectId?: number;
}
export interface ExportPropsV2 {
  filters: SimpleSearchFilter;
  match: QueryCombinerType;
  columnOrder: string[];
  projectId?: number;
}
export type TaskUpdatePhasePayload = {
  projectPhase: Pick<ProjectPhase, 'projectPhaseId'>;
} & Pick<Task, 'startDate' | 'dueDate'>;

export type AddBulkTasksPayload = Array<{
  taskName: string;
}>;

export type BulkTaskPayload = {
  tasks: Array<{ taskId: number } & Partial<Task>>;
  operationName?: string;
  bulkShiftDates?: {
    shiftValue: number;
    shiftUnit: string;
    shiftCondition: string;
  };
};

export type BulkUpdateAllTasksPayload = {
  allSelected: boolean;
  updates: Record<string, unknown>;
  operationName?: string;
  bulkShiftDates?: {
    shiftValue: number;
    shiftUnit: string;
    shiftCondition: string;
  };
  exclusionPoints: number[];
  selectedPoints: number[];
  filter: SimpleSearchFilter;
};

export type BulkTaskPayloadV2 = {
  allSelected: boolean;
  updates: {
    fields: {
      fieldId: number;
      fieldValue: string | number | boolean | null;
    }[];
  };
  exclusionPoints: number[];
  selectedPoints: number[];
  filter: SimpleSearchFilter;
};

type BulkTaskOptions = {
  isAllTask?: boolean;
};

type BulkUpdateAllTasksOptions = {
  projectId: number;
  applyToSubtask?: boolean;
};
interface PaginatedRecentlyAssignedTaskParams extends PaginatedRequestParams {
  noOfDays: number;
  filter: GroupCriteria[];
  filterType: QueryCombinerType;
}
interface PaginatedTaskParams extends PaginatedRequestParams {
  filter: GroupCriteria[];
  filterType: QueryCombinerType;
  searchValue?: string;
}
interface PaginatedTasklistParams extends PaginatedTaskParams {
  epicId?: number;
  projectId?: number;
  isEverything?: boolean;
}

interface PaginatedTaskParamsV2 extends PaginatedRequestParams {
  filter: SimpleSearchFilter;
  filterType: QueryCombinerType;
  searchValue?: string;
}

interface PaginatedTaskCountParams {
  filter: SimpleSearchFilter;
  projectId: number;
  isEverything?: boolean;
  applyToSubtask?: boolean;
}

interface PaginatedTasklistParamsV2 extends PaginatedTaskParamsV2 {
  projectId?: number;
  isEverything?: boolean;
  applyToSubtask?: boolean;
}

interface AllTasklistParams {
  filter: GroupCriteria[];
  filterType: QueryCombinerType;
  searchValue?: string;
}
interface PaginatedDashboardTaskParams extends PaginatedRequestParams {
  widgetFilter: SimpleSearchFilter;
  dashboardFilter: SimpleSearchFilter;
  widgetId: number;
}

type PaginatedTaskResponse = PaginatedResponse<Task>;

export interface DependentEntityPayload {
  entityId: number;
  dependentEntityId: number;
  dependencyType: string;
  lag: number | null;
  operation: string;
}

class TasksHttpClient extends WithNotStrategicTransformer {
  public getTaskList = async (
    projectId: number,
    config?: AxiosRequestConfig,
  ): Promise<Task[]> =>
    this.getWithTransformer<Task[]>(`/projects/${projectId}/tasks`, config);

  public async getTask(projectID: number, taskID: number): Promise<Task> {
    return this.getWithTransformer<Task>(
      `/projects/${projectID}/tasks/${taskID}`,
    );
  }

  @RliPreHook({
    event: TaskEvents.TaskUpdate,
    dataGetter: (projectId, taskId, data) => ({
      taskId,
      projectId,
      task: data,
    }),
  })
  public async updateTask(
    projectID: number,
    taskID: number,
    data,
    isSync?: boolean,
    actor?: Actor,
  ): Promise<Task> {
    const config: AxiosRequestConfig = {};

    if (actor) {
      config.headers = { 'X-Actor': JSON.stringify(actor) };
    }

    return this.putWithTransformer(
      `/projects/${projectID}/tasks/${taskID}${
        isSync ? `?isSync=${true}` : ''
      }`,
      data,
      config,
    );
  }

  @RliPreHook({
    event: TaskEvents.TaskUpdate,
    dataGetter: (projectId, taskId, data) => ({
      taskId,
      projectId,
      task: data,
    }),
  })
  public async updateTaskMini(
    projectID: number,
    taskID: number,
    data,
    isSync?: boolean,
  ): Promise<Task> {
    const config: AxiosRequestConfig = {};

    return this.putWithTransformer(
      `/projects/${projectID}/tasks/${taskID}/mini${
        isSync ? `?isSync=${true}` : ''
      }`,
      data,
      config,
    );
  }

  @RliPreHook({
    event: TaskEvents.BulkTaskUpdate,
    dataGetter: (data, options) => ({
      data,
      options,
    }),
  })
  public async bulkUpdateTask(data: BulkTaskPayload, options: BulkTaskOptions) {
    return this.put(`/bulk/tasks?isFromAllTasks=${options.isAllTask}`, data);
  }

  @RliPreHook({
    event: TaskEvents.BulkTaskUpdate,
    dataGetter: (data, options) => ({
      data,
      options,
    }),
  })
  public async bulkUpdateAllTasks(
    data: BulkUpdateAllTasksPayload,
    options: BulkUpdateAllTasksOptions,
  ) {
    const queryParams: string[] = [`projectId=${options.projectId}`];

    if (options.applyToSubtask === false) {
      queryParams.push('parentTask.isEmpty=true');
    }

    const finalUrl = `/bulk/tasks?${queryParams.join('&')}`;

    return this.put(finalUrl, data);
  }

  @RliPreHook({
    event: TaskEvents.TaskCreate,
    dataGetter: (projectId, data) => ({
      projectId,
      task: data,
    }),
  })
  public async createTask(projectID: number, data): Promise<Task> {
    return this.postWithTransformer(`/projects/${projectID}/tasks/`, data);
  }

  @RliPreHook({
    event: TaskEvents.TaskCreate,
    dataGetter: ({ projectId, data }) => ({
      projectId,
      task: data,
    }),
  })
  public async createTaskWithForm({ projectId, data }): Promise<Task> {
    return this.postWithTransformer(`/projects/${projectId}/tasks/`, data);
  }

  @RliPreHook({
    event: TaskEvents.TaskCreate,
    dataGetter: (projectId, data) => ({
      projectId,
      data,
    }),
  })
  public async duplicateTask(
    projectId: number,
    data: DuplicateTaskProps,
  ): Promise<void> {
    return this.post(`/projects/${projectId}/tasks/duplicate`, data);
  }

  @RliPreHook({
    event: TaskEvents.TaskCreate,
    dataGetter: (projectId, data) => ({
      projectId,
      data,
    }),
  })
  public async addTaskTemplatesToProject(
    projectID: number,
    data,
  ): Promise<Task[]> {
    return this.postWithTransformer(
      `/projects/${projectID}/tasks/add-from-templates`,
      data,
    );
  }

  @RliPreHook({
    event: TaskEvents.TaskCreate,
    dataGetter: ([, projectId], data) => ({
      projectId,
      data,
    }),
  })
  public async createTaskWithRequestId(
    [, projectId]: ProjectId,
    data: Record<string, unknown>,
    requestId: string,
  ): Promise<Task> {
    return this.postWithTransformer(`/projects/${projectId}/tasks/`, data, {
      headers: { 'X-Request-Id': requestId },
    });
  }

  public async getTaskFields(): Promise<AccountFields> {
    let data: AccountFields;

    try {
      data = (await InitWorkerApi.getData(
        InitWorkerRequestType.TaskFields,
      )) as AccountFields;
    } catch {
      data = await this.get<AccountFields>('/tasks/fields');
    }

    return data;
  }

  @RliPreHook({
    event: TaskEvents.TaskDelete,
    dataGetter: (projectId, taskId) => ({
      projectId,
      taskId,
    }),
  })
  public async deleteTask(
    projectID: number,
    taskID: number,
    actor?: Actor,
  ): Promise<boolean> {
    const config: AxiosRequestConfig = {};

    if (actor) {
      config.headers = { 'X-Actor': JSON.stringify(actor) };
    }

    return this.delete(`/projects/${projectID}/tasks/${taskID}/`, config);
  }

  @RliPreHook({
    event: TaskEvents.DependentTaskCreate,
    dataGetter: (projectId, taskId, data) => ({
      projectId,
      taskId,
      data,
    }),
  })
  public async createDependentTask(
    projectID: number,
    taskID: number,
    data,
    isSync?: boolean,
  ): Promise<Task> {
    return this.postWithTransformer(
      `/projects/${projectID}/tasks/${taskID}/add-dependency${
        isSync ? `?isSync=${true}` : ''
      }`,
      data,
    );
  }

  @RliPreHook({
    event: TaskEvents.DependentTaskDelete,
    dataGetter: (projectId, taskId, data) => ({
      projectId,
      taskId,
      data,
    }),
  })
  public async removeDependentTask(
    projectID: number,
    taskID: number,
    data,
  ): Promise<boolean> {
    return this.post(
      `/projects/${projectID}/tasks/${taskID}/remove-dependency`,
      data,
    );
  }

  public async updateDependentTasks(
    taskId: number,
    data: DependentEntityPayload[],
    isSync?: boolean,
  ): Promise<Task> {
    return this.postWithTransformer(
      `/tasks/${taskId}/update-dependent-tasks${
        isSync ? `?isSync=${true}` : ''
      }`,
      data,
    );
  }

  public async deleteTaskAttachment(attachmentID: number) {
    return this.delete(`attachments/${attachmentID}/`);
  }

  public async addRating(
    taskID: number,
    data: { rating: number; comment?: string },
  ): Promise<boolean> {
    return this.post(`/tasks/${taskID}/add-rating`, data);
  }

  @RliPreHook({
    event: TaskEvents.TaskUpdate,
    dataGetter: (taskId, data) => ({
      taskId,
      data,
    }),
  })
  public async updatePrivacy(
    taskID: number,
    data: { private: boolean },
  ): Promise<Task> {
    return this.postWithTransformer(`/tasks/${taskID}/update-privacy`, data);
  }

  public getAllTasksLight = async () =>
    this.getWithTransformer<Task[]>('/tasks/light');

  public searchTasks = async (filter: Filter): Promise<Task[]> => {
    const { ApiFilter } = await getSimpleSearchFilterModule();

    return this.getWithTransformer(
      `/tasks/search?${ApiFilter.toQueryStringFormat(
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        filter.criteria,
      )}&${stringify(
        {
          match: filter.criteria.length ? filter.type : null,
        },
        { skipNull: true },
      )}`,
    );
  };

  public getTasksAndMilestonesForProjects = async (
    projectId: number,
    isMilestone = false,
  ) => {
    return this.getWithTransformer(
      `/tasks/search?project.value=${projectId}&match=all${
        isMilestone ? '&type.value=MILESTONE' : ''
      }`,
    );
  };

  public getPaginatedRecentlyAssignedTaskList = async ({
    startRow = 0,
    endRow = 100,
    noOfDays = 2,
    filter,
    filterType,
    sort = 'asc',
  }: PaginatedRecentlyAssignedTaskParams): Promise<PaginatedTaskResponse> => {
    const { ApiGroupFilter } = await getSimpleSearchFilterModule();
    const filterAsString = ApiGroupFilter.toQueryStringFormatForGroups(filter);
    let url = `home/recentlyAssigned?noOfDays=${noOfDays}&sort=${sort}&offset=${startRow}&limit=${
      endRow - startRow
    }`;

    if (filter.length) {
      url += `&${filterAsString}&match=${filterType}`;
    }

    return this.get<PaginatedTaskResponse>(url);
  };

  public getPaginatedTaskList = async ({
    startRow,
    endRow,
    filter,
    sort,
    filterType,
    searchValue,
  }: PaginatedTaskParams): Promise<PaginatedTaskResponse> => {
    const { ApiGroupFilter } = await getSimpleSearchFilterModule();
    let url = '/tasks/searchlightV1?';
    const filterAsString = ApiGroupFilter.toQueryStringFormatForGroups(filter);

    if (searchValue) {
      url += `${searchValue}&`;
    }
    if (filter.length) {
      url += `${filterAsString}&match=${filterType}&`;
    }
    if (sort) {
      url += `sort=${sort}`;
    }
    url += `&offset=${startRow}&limit=${endRow - startRow}`;

    return this.get<PaginatedTaskResponse>(url).then((response) => ({
      ...response,
      data: response.data.map((task) =>
        task.fieldsAccessMeta != null
          ? {
              ...task,
              fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                task.fieldsAccessMeta,
              ),
            }
          : task,
      ),
    }));
  };

  public postFilteredTasks = async (
    filter: SimpleSearchFilter,
    startRow,
    endRow,
    sort,
    filterType,
    searchValue,
  ): Promise<PaginatedTaskResponse> => {
    let url = '/tasks/searchlightV1?';

    if (searchValue) {
      url += `${searchValue}&`;
    }

    if (sort) {
      url += `sort=${sort}`;
    }
    url += `&offset=${startRow}&limit=${endRow - startRow}`;

    return this.post<PaginatedTaskResponse, { filter: typeof filter }>(url, {
      filter,
    }).then((response) => ({
      ...response,
      data: response.data.map((task) =>
        task.fieldsAccessMeta != null
          ? {
              ...task,
              fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                task.fieldsAccessMeta,
              ),
            }
          : task,
      ),
    }));
  };

  public getPaginatedTaskListWithProject = async ({
    startRow,
    endRow,
    filter,
    sort,
    epicId,
    filterType,
    projectId,
    isEverything,
    groupBy,
    searchValue,
  }: PaginatedTasklistParams): Promise<PaginatedTaskResponse> => {
    const { ApiGroupFilter } = await getSimpleSearchFilterModule();
    const filterAsString = ApiGroupFilter.toQueryStringFormatForGroups(filter);
    const validFilterType = Object.values(QueryCombinerType).includes(
      filterType,
    )
      ? filterType
      : QueryCombinerType.All;

    const filterString = filter.length ? filterAsString : undefined;

    const url = constructUrl({
      baseURL: '/tasks/searchlightV1',
      params: {
        projectId,
        match: validFilterType,
        sort,
        epicId,
        offset: startRow,
        limit: endRow - startRow,
        groupBy,
        everything: isEverything ? true : undefined,
      },
      paramsToAddWithoutKeys: [filterString, searchValue],
    });

    return this.get<PaginatedTaskResponse>(url).then((response) => ({
      ...response,
      data: response.data.map((task) =>
        task.fieldsAccessMeta != null
          ? {
              ...task,
              fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                task.fieldsAccessMeta,
              ),
            }
          : task,
      ),
    }));
  };

  public postPaginatedTaskListCount = async ({
    filter,
    projectId,
    isEverything,
    applyToSubtask,
  }: PaginatedTaskCountParams): Promise<PaginatedTaskCountResponse> => {
    const url = constructUrl({
      baseURL: '/tasks/searchlightV1/count',
      params: {
        projectId,
        everything: isEverything ? true : undefined,
        ...(!applyToSubtask ? { 'parentTask.isEmpty': true } : {}),
      },
    });

    return this.post<PaginatedTaskCountResponse, { filter: typeof filter }>(
      url,
      { filter },
    );
  };

  public postPaginatedTaskListWithProject = async ({
    startRow,
    endRow,
    filter,
    sort,
    filterType,
    projectId,
    isEverything,
    searchValue,
    applyToSubtask,
  }: PaginatedTasklistParamsV2): Promise<PaginatedTaskResponse> => {
    const validFilterType = Object.values(QueryCombinerType).includes(
      filterType,
    )
      ? filterType
      : QueryCombinerType.All;

    const params: Record<string, any> = {
      projectId,
      match: validFilterType,
      sort,
      offset: startRow,
      limit: endRow - startRow,
      everything: isEverything ? true : undefined,
    };

    if (!applyToSubtask) {
      params['parentTask.isEmpty'] = true;
    }

    const url = constructUrl({
      baseURL: '/tasks/searchlightV1',
      params,
      paramsToAddWithoutKeys: [searchValue],
    });

    return this.post<PaginatedTaskResponse, { filter: typeof filter }>(url, {
      filter,
    }).then((response) => ({
      ...response,
      data: response.data.map((task) =>
        task.fieldsAccessMeta != null
          ? {
              ...task,
              fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                task.fieldsAccessMeta,
              ),
            }
          : task,
      ),
    }));
  };

  public postPaginatedTaskListWithSubtasks = async ({
    startRow,
    endRow,
    filter,
    sort,
    filterType,
    projectId,
    isEverything,
    searchValue,
  }: PaginatedTasklistParamsV2): Promise<PaginatedTaskResponse> => {
    const validFilterType = Object.values(QueryCombinerType).includes(
      filterType,
    )
      ? filterType
      : QueryCombinerType.All;

    const url = constructUrl({
      baseURL: '/tasks/searchlightV1',
      params: {
        projectId,
        match: validFilterType,
        sort,
        offset: startRow,
        limit: endRow - startRow,
        everything: isEverything ? true : undefined,
      },
      paramsToAddWithoutKeys: [searchValue],
    });

    return this.post<PaginatedTaskResponse, { filter: typeof filter }>(url, {
      filter,
    }).then((response) => ({
      ...response,
      data: response.data.map((task) =>
        task.fieldsAccessMeta != null
          ? {
              ...task,
              fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                task.fieldsAccessMeta,
              ),
            }
          : task,
      ),
    }));
  };

  public getAllTaskListWithProject = async ({
    filter,
    filterType,
    searchValue,
  }: AllTasklistParams): Promise<PaginatedTaskResponse> => {
    const { ApiGroupFilter } = await getSimpleSearchFilterModule();
    const filterAsString = ApiGroupFilter.toQueryStringFormatForGroups(filter);
    const validFilterType = Object.values(QueryCombinerType).includes(
      filterType,
    )
      ? filterType
      : QueryCombinerType.All;

    const filterString = filter.length ? filterAsString : undefined;
    const match = filter.length ? validFilterType : undefined;

    const url = constructUrl({
      baseURL: '/tasks/searchlightV1',
      params: {
        match,
      },
      paramsToAddWithoutKeys: [filterString, searchValue],
    });

    return this.get<PaginatedTaskResponse>(url).then((response) => ({
      ...response,
      data: response.data.map((task) =>
        task.fieldsAccessMeta != null
          ? {
              ...task,
              fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                task.fieldsAccessMeta,
              ),
            }
          : task,
      ),
    }));
  };

  public getSubTasks = (taskId: number): Promise<Task[]> => {
    return this.get<Task[]>(`/tasks/${taskId}/get-subtasks`).then((tasks) =>
      tasks.map((task) =>
        task.fieldsAccessMeta != null
          ? {
              ...task,
              fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                task.fieldsAccessMeta,
              ),
            }
          : task,
      ),
    );
  };

  public searchTasksForTimesheet = async (
    fromDateInMillisecond: Moment,
    toDateInMillisecond: Moment,
    projectId?: number,
  ): Promise<Task[]> => {
    return this.get(
      `/tasks/timesheet?${stringify({
        fromDate: fromDateInMillisecond.valueOf(),
        toDate: toDateInMillisecond.valueOf(),
        projectId,
      })}`,
    );
  };

  public getPaginatedTaskListForDashbaord = async ({
    dashboardFilter,
    widgetFilter,
    startRow,
    endRow,
    sort,
    widgetId,
  }: PaginatedDashboardTaskParams): Promise<PaginatedTaskResponse> => {
    let url = `/widgets/${widgetId}/drill-down-items?`;

    if (sort) {
      url += `sort=${sort}`;
    }
    url += `&offset=${startRow}&limit=${endRow - startRow}`;

    return this.post(url, {
      drillDownFilter: widgetFilter,
      dashboardFilter,
    });
  };

  public updateSubTaskOrder = (
    taskId: number,
    payload: UpdateSubTaskOrderPayload,
  ): Promise<void> => {
    return this.put(`/tasks/${taskId}/update-subtask-order`, payload);
  };

  public updatePhase = (
    taskId: number,
    payload: TaskUpdatePhasePayload,
  ): Promise<Task> => {
    return this.putWithTransformer(`/tasks/${taskId}/update-phase`, payload);
  };

  public exportAsCSVAsyncForGroupType = async ({
    filters,
    match,
    columnOrder,
    projectId,
  }: GroupExportProps): Promise<ExportResponse> => {
    const { ApiFilter, ApiGroupFilter } = await getSimpleSearchFilterModule();
    const filterString = ApiGroupFilter.toQueryStringFormatForGroups(filters);
    const filterObject = ApiFilter.convertQueryStringToObject(filterString);

    let filterBy: FilterBy = {
      match,
      ...filterObject,
    };

    if (projectId) {
      filterBy = { ...filterBy, projectId };
    }
    const request: ExportRequest = {
      filterBy,
      columnOrder,
    };

    const data: ExportPayload = {
      statusType: ExportStatusType.TaskExport,
      statusMeta: { request },
    };

    return this.post<ExportResponse, ExportPayload>('async/status', data);
  };

  public exportAsCSVAsyncForGroupTypeV2 = ({
    filters,
    match,
    columnOrder,
    projectId,
  }: GroupExportPropsV2): Promise<ExportResponse> => {
    match = filters.match;
    const filterBy: FilterBy = {
      match,
    };

    if (projectId) {
      filterBy.projectId = projectId;
    }

    const request: ExportRequest = {
      filterBy,
      columnOrder,
    };

    const data: ExportPayload = {
      statusType: ExportStatusType.TaskExport,
      statusMeta: { filter: filters, request },
    };

    return this.post<ExportResponse, ExportPayload>('async/status', data);
  };

  public exportAsCSVAsyncForNonGroup = async ({
    filters,
    match,
    columnOrder,
    projectId,
  }: ExportProps): Promise<ExportResponse> => {
    const { ApiFilter } = await getSimpleSearchFilterModule();
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    const filterString = ApiFilter.toQueryStringFormat(filters);
    const filterObject = ApiFilter.convertQueryStringToObject(filterString);

    let filterBy: FilterBy = {
      match,
      ...filterObject,
    };

    if (projectId) {
      filterBy = { ...filterBy, projectId };
    }
    const request: ExportRequest = {
      filterBy,
      columnOrder,
    };

    const data: ExportPayload = {
      statusType: ExportStatusType.TaskExport,
      statusMeta: { request },
    };

    return this.post<ExportResponse, ExportPayload>('async/status', data);
  };

  public exportAsCSVAsyncForNonGroupV2 = ({
    filters,
    match,
    columnOrder,
    projectId,
    applyToSubtask,
  }: ExportPropsV2 & { applyToSubtask?: boolean }): Promise<ExportResponse> => {
    const filterBy: FilterBy = {
      match,
    };

    if (projectId) {
      filterBy.projectId = projectId;
    }

    if (
      !applyToSubtask &&
      ((filters.nativeFields?.length ?? 0) > 0 ||
        (filters.customFields?.length ?? 0) > 0 ||
        (filters.nestedFilter?.length ?? 0) > 0)
    ) {
      filterBy['parentTask.isEmpty'] = 'true';
    }

    const request: ExportRequest = {
      filterBy,
      columnOrder,
    };

    const data: ExportPayload = {
      statusType: ExportStatusType.TaskExport,
      statusMeta: { filter: filters, request },
    };

    return this.post<ExportResponse, ExportPayload>('async/status', data);
  };

  public async addMultipleSubtasks(
    parentTaskId: number,
    data: AddBulkTasksPayload,
  ) {
    return this.post(`tasks/${parentTaskId}/create-bulk-subtasks`, data);
  }

  public async addMultipleTasks(
    parentTaskId: number,
    data: AddBulkTasksPayload,
  ) {
    return this.post(`projects/${parentTaskId}/tasks/bulk-create`, data);
  }

  public async logFilters(filter: Filter) {
    const { ApiFilter } = await getSimpleSearchFilterModule();
    let url = '/tasks/searchlightV1?';
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    const filterAsString = ApiFilter.toQueryStringFormat(filter.criteria);

    if (filter.criteria.length) {
      url += `${filterAsString}&match=${filter.type}&`;
    }
    this.get(url);
  }

  public async removeAsSubtask(taskId: number) {
    return this.put(`/tasks/remove-as-subtask/${taskId}`);
  }

  public async getBulkTaskDetails(taskIds: number[]): Promise<Task[]> {
    return this.post('/tasks/search/getTaskByIds', { taskIds }).then(
      (tasks: unknown) => {
        if (!Array.isArray(tasks)) {
          throw new Error('Invalid response: tasks is not an array');
        }

        return tasks.map((task: any) => {
          return task && task.fieldsAccessMeta != null
            ? {
                ...task,
                fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                  task.fieldsAccessMeta,
                ),
              }
            : task;
        });
      },
    );
  }

  public async getTaskFieldValues(
    taskIds: number[],
  ): Promise<FieldsWithUniqueValuesForBulkEditor[]> {
    return this.post('/tasks/fields/unique-values', taskIds);
  }

  public async getSubtaskCount(projectId: number): Promise<number> {
    const url = constructUrl({
      baseURL: '/tasks/searchlightV1/subtask-count',
      params: {
        projectId,
      },
    });

    return this.get<number>(url);
  }

  public async getTaskDetailsDefaultFields({
    projectId,
    taskId,
  }: {
    projectId: number;
    taskId: number;
  }): Promise<Task> {
    return this.getWithTransformer(
      `/projects/${projectId}/tasks/${taskId}/get-default-fields`,
    );
  }

  public async getTaskDetailsAdditionalFields({
    projectId,
    taskId,
  }: {
    projectId: number;
    taskId: number;
  }): Promise<Task> {
    return this.getWithTransformer(
      `/projects/${projectId}/tasks/${taskId}/get-additional-fields`,
    );
  }
}

export const TaskAPI = new TasksHttpClient();
